import { useState } from 'react';
import PropTypes from 'prop-types';
import Skeleton from 'react-loading-skeleton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion, faDownload } from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { RANKING_TYPES, getRankingRows, getName, getMetric, formatMetric } from '../utils/rankingTableHelpers';
import '../styles/companionAppUsageReport.css';

const escapeCsvValue = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const TopUsageRanking = ({ rankings, isLoading = false, initialType = 'districts', onExport = null, ferpaEnabled = true }) => {
  const [selectedType, setSelectedType] = useState(initialType);

  if (isLoading) {
    return (
      <section className="companion-report-card companion-ranking-card mt-3">
        <Skeleton width="160px" height="20px" className="mb-3" />
        {[...Array(10)].map((_, index) => (
          <Skeleton key={index} height="29px" className="mb-2" />
        ))}
      </section>
    );
  }

  const availableRankingTypes = Array.isArray(rankings)
    ? [[initialType, RANKING_TYPES[initialType]]]
    : Object.entries(RANKING_TYPES).filter(([type]) => getRankingRows(rankings, type).length > 0);
  const activeType = availableRankingTypes.some(([type]) => type === selectedType)
    ? selectedType
    : availableRankingTypes[0]?.[0] ?? initialType;
  const activeConfig = RANKING_TYPES[activeType];
  const rows = getRankingRows(rankings, activeType);
  const visibleRows = rows.slice(0, 10);

  const handleExport = () => {
    if (onExport) {
      onExport(activeType, rows);
      return;
    }

    const csvRows = [
      [`Top ${activeConfig.nameHeader}s Usage Ranking `],
      [],
      ['Rank', activeConfig.nameHeader, 'Sessions / User', 'Avg Session (min)', 'Engagement Score'],
      ...rows.map((row, index) => [
        row.rank ?? index + 1,
        getName(row, activeType, ferpaEnabled),
        getMetric(row, 'sessionsPerUser', 'sessionsPerUserCount'),
        getMetric(row, 'averageSessionLength', 'avgSessionMinutes', 'averageSessionMinutes'),
        getMetric(row, 'engagementScore')
      ])
    ];
    const csvContent = csvRows.map((row) => row.map(escapeCsvValue).join(',')).join('\n');
    const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const csvUrl = URL.createObjectURL(csvBlob);
    const csvLink = document.createElement('a');
    csvLink.href = csvUrl;
    csvLink.download = `Top_${activeConfig.nameHeader}s_Usage_Ranking.csv`;
    csvLink.click();
    URL.revokeObjectURL(csvUrl);
  };

  return (
    <section className="companion-report-card page-break-avoid companion-ranking-card mt-3">
      <div className="companion-ranking-header">
        <div>
          <div className="companion-ranking-title-row">
            <h2 className="companion-card-title">Top Usage Ranking</h2>
            <div className="companion-ranking-tabs" role="tablist" aria-label="Usage ranking type">
              {availableRankingTypes.map(([type, config]) => (
                <button
                  key={type}
                  type="button"
                  role="tab"
                  aria-selected={activeType === type}
                  className={activeType === type ? 'active' : ''}
                  onClick={() => setSelectedType(type)}
                >
                  {config.label}
                </button>
              ))}
            </div>
            <OverlayTrigger
              placement="top"
              overlay={(
                <Tooltip id="top-usage-ranking-tooltip" className="additional-options-tooltip">
                  <h6>{activeConfig.label}</h6>
                  <span>
                    {activeConfig.helpText}
                  </span>
                  <br></br><br></br>
                  <span>
                    Engagement Score = Sessions per User x Average Session Length (total engaged time). Rankings are
                    sorted from highest to lowest engagement score.
                  </span>
                  <br></br><br></br>
                  <span className="mb-0">
                    The panel shows the top 10 rows. Use Export all to CSV for the complete list.
                  </span>
                </Tooltip>
              )}
            >
              <FontAwesomeIcon className="circle-question" icon={faCircleQuestion} />
            </OverlayTrigger>
          </div>
          <p className="companion-ranking-description">
            Showing the top 10 {activeType} on the platform. Export to CSV for the full list.
          </p>
        </div>

        <button
          type="button"
          className="btn-castleOrangeLite btn-link p-0 companion-ranking-export"
          onClick={handleExport}
        >
          <FontAwesomeIcon icon={faDownload} className="me-2" />
          Export all to CSV
        </button>
      </div>

      <div className="table-responsive companion-ranking-table-wrap">
        <table className="companion-ranking-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{activeConfig.nameHeader}</th>
              <th>Sessions / User</th>
              <th>Avg Session (min)</th>
              <th>Engagement Score</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => (
              <tr key={row.id ?? row.districtId ?? row.schoolId ?? row.studentId ?? `${activeType}-${index}`}>
                <td>{row.rank ?? index + 1}</td>
                <td>{getName(row, activeType, ferpaEnabled)}</td>
                <td>{formatMetric(getMetric(row, 'sessionsPerUser', 'sessionsPerUserCount'))}</td>
                <td>{formatMetric(getMetric(row, 'averageSessionLength', 'avgSessionMinutes', 'averageSessionMinutes'))}</td>
                <td>{formatMetric(getMetric(row, 'engagementScore'))}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td className="companion-empty-state" colSpan="5">No ranking data available.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

TopUsageRanking.propTypes = {
  rankings: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.shape({
      districts: PropTypes.arrayOf(PropTypes.object),
      schools: PropTypes.arrayOf(PropTypes.object),
      students: PropTypes.arrayOf(PropTypes.object)
    })
  ]),
  isLoading: PropTypes.bool,
  initialType: PropTypes.oneOf(Object.keys(RANKING_TYPES)),
  onExport: PropTypes.func,
  ferpaEnabled: PropTypes.bool
};

export default TopUsageRanking;